export const SUPPORTED_LANGUAGES = ["en", "sv"] as const;
export type CmsLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const SOURCE_LANGUAGE: CmsLanguage = "en";
export const LANGUAGE_STORAGE_KEY = "foyer.language";

/** Each language's name, written in that language. */
export const LANGUAGE_NAMES: Record<CmsLanguage, string> = {
  en: "English",
  sv: "Svenska",
};

/** The BCP 47 locale `Intl` formats with for each CMS language. */
export const INTL_LOCALES: Record<CmsLanguage, string> = {
  en: "en-GB",
  sv: "sv-SE",
};

export function isCmsLanguage(value: unknown): value is CmsLanguage {
  return (
    typeof value === "string" &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
  );
}

/**
 * From `VITE_CMS_LANGUAGES`. Unset: English only in a production build, every
 * language on the dev server. English is always offered, and first.
 */
export function enabledLanguages(
  raw: string | undefined = import.meta.env.VITE_CMS_LANGUAGES,
  devServer: boolean = import.meta.env.DEV
): CmsLanguage[] {
  const requested =
    raw === undefined || raw.trim() === ""
      ? devServer
        ? [...SUPPORTED_LANGUAGES]
        : [SOURCE_LANGUAGE]
      : raw.split(",").map((s) => s.trim().toLowerCase());
  const known = requested.filter(isCmsLanguage);
  return [
    SOURCE_LANGUAGE,
    ...SUPPORTED_LANGUAGES.filter(
      (l) => l !== SOURCE_LANGUAGE && known.includes(l)
    ),
  ];
}

function readStored(): string | null {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** localStorage, then the browser's languages, then English. */
export function initialLanguage(
  enabled: readonly CmsLanguage[],
  stored: string | null = readStored(),
  browser: readonly string[] = typeof navigator === "undefined"
    ? []
    : navigator.languages?.length
      ? navigator.languages
      : [navigator.language]
): CmsLanguage {
  if (isCmsLanguage(stored) && enabled.includes(stored)) return stored;
  for (const tag of browser) {
    const base = tag?.split("-")[0]?.toLowerCase();
    if (isCmsLanguage(base) && enabled.includes(base)) return base;
  }
  return SOURCE_LANGUAGE;
}
